import type { ClientToolSpec, Locale, PageAgentInfo, PageMode } from "./types";
import type { Sensitivity } from "./desk";

/**
 * The wire protocol between a page (an opaque-origin iframe) and its host. The page announces itself with `hello` on
 * window.postMessage; the host checks the frame is its own and answers `welcome` with a MessageChannel port. Everything after that
 * travels on the port, which no other window can reach. Binary values (files, audio, Arrow buffers) cross as ArrayBuffers.
 */
export const PROTOCOL_VERSION = 1;

export interface HelloMessage {
  ncp: typeof PROTOCOL_VERSION;
  kind: "hello";
  page: string;
  version: number;
  entry: string;
  /**
   * The run token of the frame's URL. The host only answers documents it served: a page the frame was navigated to elsewhere cannot know
   * the token (run documents send no referrer), so it cannot claim the bridge.
   */
  nonce: string | null;
  /** Random per document: the greeting repeats until answered, and the host answers each document once. */
  instance: string;
}

export interface WelcomeMessage {
  ncp: typeof PROTOCOL_VERSION;
  kind: "welcome";
  session: HostSession;
}

/** What the host tells the page about the session; credentials stay in the host. */
export interface HostSession {
  page: { key: string; version: number; name: string; nameAr?: string | null; draft: boolean };
  space: string;
  slot: string;
  matterId: string | null;
  locale: Locale;
  theme: string;
  mode: PageMode;
  user: { id: string | null; name: string | null };
  capabilities: string[];
  grants: string[];
  agents: PageAgentInfo[];
  clientTools: ClientToolSpec[];
  /** The page's own route inside the host URL (/p/:page/<path>?<params>). */
  route: PageRoute;
  // ---- ECONIC additions (SPEC C36, shell-ia §15.2). All optional, so PROTOCOL_VERSION stays 1 and older hosts and pages keep working.
  /** The person's display settings from the host's accessibility menu; later changes arrive as the `display` event. */
  display?: HostDisplay;
  /** The desk's classification floor (space manifest `econic.classification`); presentation mode draws it in the slide footer. */
  classification?: Sensitivity;
  /** `header: "host"`: the host draws the desk header (`desk.header` state), so the page hides its own title block. */
  chrome?: { header: "host" };
  /** Read-only capability results the host fetched while the frame loaded, keyed `${capability} ${JSON.stringify(input)}`; later ones arrive as the `prefetched` event. */
  prefetched?: Record<string, unknown>;
}

/**
 * What the host's accessibility menu applies to frames (shell-ia §9.1). The ECONIC host sends every field; older hosts send none, so a page
 * reads each one with a default. Later changes arrive as the `display` event with the whole object.
 */
export interface HostDisplay {
  contrast: "normal" | "more";
  motion: "reduce" | "allow";
  transparency: "reduce" | "allow";
  density: "comfortable" | "compact";
  /** The workspace's light or dark appearance (`ui.theme` resolved against the device); page-kit sets `html[data-scheme]`. */
  scheme?: "light" | "dark";
  /** The type-token step of the host (`html[data-text-size]`); the host no longer zooms, so frames scale their own type. */
  text?: "default" | "large" | "larger";
  /**
   * The device draws in forced colours (Windows High Contrast; `@media (forced-colors: active)`), APPLE-SPEC §9.2. CSS inside the frame
   * sees the media query itself; this is for what scripts draw (canvas charts, maps), which must then use the system colours.
   */
  forcedColors?: boolean;
}

/** `ncp.host("classification", …)`: raise the host banner while the page shows that material; `level: null` drops the raise. */
export interface HostClassificationArgs {
  level: Sensitivity | null;
  /** A short name of what carries the marking ("Ministry briefing · Hormuz"), shown in the banner's popover. */
  source?: string;
}

export interface PageRoute {
  path: string;
  params: Record<string, string>;
}

/**
 * `activity`: a heartbeat while the person works inside the frame (at most one a minute), so the host's idle timer counts it.
 * `classification`: the page raises the host banner to a level (`{ level: Sensitivity, source?: string }`) while it shows that material.
 */
export type HostOp =
  | "toast" | "confirm" | "navigate" | "open" | "title" | "resize" | "state" | "tools" | "mode" | "route" | "media.record" | "files.pick"
  | "download" | "threads" | "messages" | "print" | "activity" | "classification";

/**
 * viewport: for an auto-height frame, the part of it on screen ({ top, height } in the frame's pixels), so overlays open where the reader is.
 * display: a new `HostDisplay`. prefetched: prefetch results that arrived after the welcome (same keys as `HostSession.prefetched`).
 */
export type HostEventName = "locale" | "theme" | "route" | "matter" | "visibility" | "platform" | "mode" | "session" | "viewport" | "display" | "prefetched";

export type PageToHost =
  | { kind: "ready" }
  | { kind: "call"; id: number; capability: string; input?: unknown }
  | { kind: "stream"; id: number; capability: string; input?: unknown }
  /** A capability's table as an Arrow IPC stream; the answer is the bytes (an ArrayBuffer), moved rather than copied. */
  | { kind: "arrow"; id: number; capability: string; input?: unknown }
  | { kind: "chat"; id: number; agent: string; message: string; threadId?: string | null; state?: unknown }
  | { kind: "cancel"; id: number }
  | { kind: "host"; id: number; op: HostOp; args?: unknown }
  | { kind: "tool-result"; callId: string; result?: unknown; error?: string };

export type HostToPage =
  | { kind: "result"; id: number; result: unknown }
  | { kind: "error"; id: number; error: string; code?: string; status?: number }
  | { kind: "chunk"; id: number; chunk: unknown }
  | { kind: "end"; id: number }
  | { kind: "tool"; callId: string; tool: string; arguments: unknown }
  | { kind: "event"; name: HostEventName; payload: unknown };

/**
 * The host-fed module cache (shell-ia §15.5 F2). A run document opened with `HostModules` posts this to `parent` before it loads its
 * modules; the host answers once per document, on the MessageChannel the message carries, with `ModulesReply` for the vendor and asset
 * URLs it holds. `nonce` is the frame's run token, checked like `hello`'s. The host never stores bytes a frame sends.
 */
export interface ModulesRequestMessage {
  ncp: typeof PROTOCOL_VERSION;
  kind: "modules";
  nonce: string | null;
  urls: string[];
}

/** The answer to `ModulesRequestMessage`: module bytes by URL (moved, not copied); URLs the host does not hold are left out. */
export interface ModulesReply {
  ncp: typeof PROTOCOL_VERSION;
  kind: "modules";
  files: Record<string, ArrayBuffer>;
}

/** The key of a prefetched result in `HostSession.prefetched` and the `prefetched` event, the same as page-kit's cache key. */
export const prefetchKey = (capability: string, input: unknown): string => `${capability} ${JSON.stringify(input ?? null)}`;

export const isModulesRequest = (data: unknown): data is ModulesRequestMessage =>
  typeof data === "object" && data !== null && (data as ModulesRequestMessage).ncp === PROTOCOL_VERSION && (data as ModulesRequestMessage).kind === "modules"
  && Array.isArray((data as ModulesRequestMessage).urls);

export const isHello = (data: unknown): data is HelloMessage =>
  typeof data === "object" && data !== null && (data as HelloMessage).ncp === PROTOCOL_VERSION && (data as HelloMessage).kind === "hello";

export const isWelcome = (data: unknown): data is WelcomeMessage =>
  typeof data === "object" && data !== null && (data as WelcomeMessage).ncp === PROTOCOL_VERSION && (data as WelcomeMessage).kind === "welcome";

/** ArrayBuffers inside a message (a few levels deep), so they move instead of being copied. */
export function transferables(value: unknown, depth = 0, found: Transferable[] = []): Transferable[] {
  if (depth > 5 || value === null || typeof value !== "object") return found;
  if (value instanceof ArrayBuffer) {
    found.push(value);
    return found;
  }
  if (ArrayBuffer.isView(value)) {
    if (value.buffer instanceof ArrayBuffer && value.byteOffset === 0 && value.byteLength === value.buffer.byteLength) found.push(value.buffer);
    return found;
  }
  if (Array.isArray(value)) {
    for (const v of value.slice(0, 200)) transferables(v, depth + 1, found);
    return found;
  }
  if (Object.getPrototypeOf(value) === Object.prototype) for (const v of Object.values(value).slice(0, 200)) transferables(v, depth + 1, found);
  return found;
}
